// components/ui/select.jsx
import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { DateTimePicker } from "./dateTimePicker";

const Select = React.forwardRef(
  (
    {
      className = "",
      options = [],
      value,
      onChange,
      placeholder = "Select an option...",
      allowCustomInput = false,
      customInputPlaceholder = "Enter custom reason...",
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [customValue, setCustomValue] = useState("");
    const [showCustomInput, setShowCustomInput] = useState(false);
    const [showDateTimePicker, setShowDateTimePicker] = useState(false);
    const [selectedDateTime, setSelectedDateTime] = useState("");
    const selectRef = useRef(null);
    const customInputRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (selectRef.current && !selectRef.current.contains(event.target)) {
          setIsOpen(false);
          setShowCustomInput(false);
          setShowDateTimePicker(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    useEffect(() => {
      if (showCustomInput && customInputRef.current) {
        customInputRef.current.focus();
      }
    }, [showCustomInput]);

    const handleOptionClick = (option) => {
      if (option.value === "others" && allowCustomInput) {
        setShowCustomInput(true);
        setIsOpen(false);
      } else if (option.value === "set_preferred_time") {
        setShowDateTimePicker(true);
        setIsOpen(false);
      } else {
        onChange(option.value);
        setIsOpen(false);
      }
    };

    const handleCustomSubmit = () => {
      if (customValue.trim()) {
        onChange(customValue.trim());
        setCustomValue("");
        setShowCustomInput(false);
      }
    };

    const handleDateTimeSubmit = (dateTime) => {
      setSelectedDateTime(dateTime);
      onChange("set_preferred_time", dateTime);
      setShowDateTimePicker(false);
    };

    const handleDateTimeClose = () => {
      setShowDateTimePicker(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleCustomSubmit();
      } else if (e.key === "Escape") {
        setShowCustomInput(false);
        setCustomValue("");
      }
    };

    const getDisplayValue = () => {
      if (value === "set_preferred_time" && selectedDateTime) {
        const date = new Date(selectedDateTime);
        const localDate = new Date(
          date.getTime() - date.getTimezoneOffset() * 60000
        );
        return `Set Preferred Time - ${localDate.toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })}`;
      }
      if (value && !options.find((opt) => opt.value === value)) {
        return value; // Custom value
      }
      const selectedOption = options.find((opt) => opt.value === value);
      return selectedOption ? selectedOption.label : placeholder;
    };

    return (
      <div className="relative" ref={selectRef}>
        <button
          type="button"
          className={`
          flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50
          ${className}
        `}
          onClick={() => setIsOpen(!isOpen)}
          {...props}
        >
          <span className={value ? "text-foreground" : "text-muted-foreground"}>
            {getDisplayValue()}
          </span>
          <ChevronDown
            className={`h-4 w-4 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>

        {isOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-input rounded-md shadow-lg max-h-60 overflow-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                onClick={() => handleOptionClick(option)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {showCustomInput && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-input rounded-md shadow-lg p-3">
            <input
              ref={customInputRef}
              type="text"
              placeholder={customInputPlaceholder}
              value={customValue}
              onChange={(e) => setCustomValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomValue("");
                }}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCustomSubmit}
                disabled={!customValue.trim()}
                className="px-3 py-1 text-sm bg-black text-white rounded hover:bg-black/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {showDateTimePicker && (
          <DateTimePicker
            value={selectedDateTime}
            onChange={handleDateTimeSubmit}
            onClose={handleDateTimeClose}
          />
        )}
      </div>
    );
  }
);

Select.displayName = "Select";

export { Select };
